import './main.css';

import { createRoot, Root, RootOptions } from 'react-dom/client';

import {
    CommunicationChannelsErrorDialog,
    CommunicationChannelsErrorDialogProps,
} from '@/dialogs/communication-channels-error/CommunicationChannelsErrorDialog';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { PriorItemDialog, PriorItemDialogProps} from '@/dialogs/prior-item/PriorItemDialog.tsx';

import { ItemDetails, ItemDetailsComponentProps } from './components/ItemDetails/ItemDetails.tsx';
import { ItemsList, ItemsListComponentProps } from './components/itemsList/itemsList.tsx';
import { ReactBridgePageWrapper } from './components/ReactBridgePageWrapper.tsx';
import {
    CompareItemsDialog,
    CompareItemsDialogProps,
} from './dialogs/compare-items/CompareItemsDialog.tsx';
import {
    DataModelExchangeDialog,
    DataModelExchangeDialogProps,
} from './dialogs/data-model-exchange/DataModelExchangeDialog.tsx';
import {
    ExportMediaAssetDialog,
    ExportMediaAssetDialogProps,
} from './dialogs/export-media-asset/ExportMediaAssetDialog.tsx';
import { FocusAttributeProvider } from './dialogs/item-editor/Context/FocusAttributeProvider.tsx';
import { ItemEditorDialog, ItemEditorDialogProps } from './dialogs/item-editor/ItemEditorDialog.tsx';
import { MassUpdateDialog, MassUpdateDialogProps } from './dialogs/mass-update/MassUpdateDialog.tsx';
import {
    PublishToRecipientsDialog,
    PublishToRecipientsDialogProps,
} from './dialogs/publish-to-recipients/PublishToRecipientsDialog.tsx';
import { BrowsePage, SearchFilterBarHost } from './pages/browse';
import { FilterBuilder, FilterBuilderProps } from './pages/browse/components/SearchFilterBar/FilterBuilder';
import BulkSubscriptionsPage from './pages/bulk-subscriptions/BulkSubscriptionsPage.tsx';
import EnhancedContentPage from './pages/enhanced-content/EnhancedContentPage.tsx';
import ExportPage from './pages/export/ExportPage.tsx';
import { ItemDetailsPage } from './pages/item-details/ItemDetailsPage.tsx';
import MonitorPage from './pages/monitor/MonitorPage';
import { PublicationsPage } from './pages/publication';
import ExecutedPublication from './pages/publication/ExecutedPublication.tsx';
import { hasSettingFeature } from './services/angularjs-bridge';
import { ComponentProps } from './types/ComponentProps.ts';

// Extend the browser's native window object with properties that we want to expose
// from our React app to the outer (legacy) world.
export interface CustomWindow extends Window {
    reactCreateComponent: (
        container: Element | DocumentFragment,
        options?: RootOptions,
    ) => Root;

    // A collection for primitive components
    reactComponents: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (props: any) => JSX.Element;
    };

    // A collection for 'Page' components
    reactPages: {
        [key: string]: (props: ComponentProps) => JSX.Element;
    };

    // A collection for 'Dialog' components
    reactDialogs: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (props: any) => JSX.Element;
    };

    // Base url used with our exposed open micro frontends
    __BYRD_BASE_URL__: string;
    // Marker used mainly for React.Portal
    __BYRD_MICRO_FRONTEND__: boolean;

    // Shared DB object with AngularJS app.
    __IDB: IDBDatabase;

    // Control flag over enabled logging streams (enabled on development)
    __DEBUG_LOGS_ENABLED?: boolean;
}

declare const window: CustomWindow;

// Attaching the createRoot to the global window instance to be used by the outer (legacy) world
window.reactCreateComponent = createRoot;

// A collection of components that are attached to the global window instance
window.reactComponents = {};

// A collection of pages that are attached to the global window instance
window.reactPages = {};
window.reactPages['MonitorPage'] = () => (
    <ReactBridgePageWrapper>
        <MonitorPage />
    </ReactBridgePageWrapper>
);

window.reactPages['ExportPage'] = () => (
    <ReactBridgePageWrapper>
        <ExportPage />
    </ReactBridgePageWrapper>
);

window.reactPages['EnhancedContentPage'] = () =>
    <ReactBridgePageWrapper>
        <EnhancedContentPage />
    </ReactBridgePageWrapper>;

window.reactPages['BulkSubscriptionsPage'] = () =>
    <ReactBridgePageWrapper>
        <BulkSubscriptionsPage />
    </ReactBridgePageWrapper>;

// LAX-10128: BrowsePage wraps the React hit list — same NEXT_HIT_LIST gate as
// items-controller.js#initReactBridge and browse.tpl.html#items-list. Evaluated
// lazily via a getter so organization settings are loaded before the check.
Object.defineProperty(window.reactPages, 'BrowsePage', {
    enumerable: true,
    configurable: true,
    get(): (() => JSX.Element) | undefined {
        if (!hasSettingFeature('NEXT_HIT_LIST')) {
            return undefined;
        }
        return () => (
            <ReactBridgePageWrapper>
                <FocusAttributeProvider>
                    <BrowsePage />
                </FocusAttributeProvider>
            </ReactBridgePageWrapper>
        );
    },
});

// LAX-10148: PublicationsPage composes scheduled + executed publications —
// same NEXT_PUBLICATIONS gate as publications.tpl.html. Evaluated lazily
// via a getter so organization settings are loaded before the check.
Object.defineProperty(window.reactPages, 'PublicationsPage', {
    enumerable: true,
    configurable: true,
    get(): (() => JSX.Element) | undefined {
        if (!hasSettingFeature('NEXT_PUBLICATIONS')) {
            return undefined;
        }
        return () => (
            <ReactBridgePageWrapper>
                <PublicationsPage />
            </ReactBridgePageWrapper>
        );
    },
});

window.reactComponents['ExecutedPublication'] = (
    props: any,
) =>
    <ReactBridgePageWrapper>
        <ExecutedPublication {...props}/>
    </ReactBridgePageWrapper>;

// A collection of dialogs that are attached to the global window instance
window.reactDialogs = {};
window.reactDialogs['ExportMediaAssetDialog'] = (
    props: ExportMediaAssetDialogProps,
) => (
    <ReactBridgePageWrapper>
        <ExportMediaAssetDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['DataModelExchangeDialog'] = (
    props: DataModelExchangeDialogProps,
) => (
    <ReactBridgePageWrapper>
        <DataModelExchangeDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['PriorItemDialog'] = (props: PriorItemDialogProps) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <PriorItemDialog {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

window.reactDialogs['CommunicationChannelsErrorDialog'] = (
    props: CommunicationChannelsErrorDialogProps,
) => (
    <ReactBridgePageWrapper>
        <CommunicationChannelsErrorDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['CompareItemsDialog'] = (
    props: CompareItemsDialogProps,
) => (
    <ReactBridgePageWrapper>
        <CompareItemsDialog {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['MassUpdateDialog'] = (
    props: MassUpdateDialogProps,
) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <MassUpdateDialog {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

window.reactDialogs['ItemEditorDialog'] = (
    props: ItemEditorDialogProps,
) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <ItemEditorDialog {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

window.reactDialogs['ConfirmationModal'] = (
    props: any,
) => (
    <ReactBridgePageWrapper>
        <ConfirmationModal {...props} />
    </ReactBridgePageWrapper>
);

window.reactDialogs['PublishToRecipientsDialog'] = (
    props: PublishToRecipientsDialogProps,
) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <PublishToRecipientsDialog {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

Object.defineProperty(window.reactDialogs, 'FilterBuilder', {
    enumerable: true,
    configurable: true,
    get(): ((props: FilterBuilderProps) => JSX.Element) | undefined {
        if (!hasSettingFeature('NEXT_HIT_LIST')) {
            return undefined;
        }
        return (props: FilterBuilderProps) => (
            <ReactBridgePageWrapper>
                <FocusAttributeProvider>
                    <FilterBuilder {...props} />
                </FocusAttributeProvider>
            </ReactBridgePageWrapper>
        );
    },
});

window.reactComponents['ItemDetails'] = (
    props: ItemDetailsComponentProps
) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <ItemDetails {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

window.reactComponents['ItemDetailsPage'] = () => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <ItemDetailsPage/>
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

window.reactComponents['ItemsList'] = (
    props: ItemsListComponentProps
) => (
    <ReactBridgePageWrapper>
        <FocusAttributeProvider>
            <ItemsList {...props} />
        </FocusAttributeProvider>
    </ReactBridgePageWrapper>
);

// LAX-10134: React Filters dropdown on `/browse` while LAX-10137 still
// owns the full page. Mounted into `#search-filter-bar` from
// items-controller.js when NEXT_HIT_LIST is on.
window.reactComponents['SearchFilterBar'] = () => (
    <ReactBridgePageWrapper>
        <SearchFilterBarHost />
    </ReactBridgePageWrapper>
);
